/**
 * Deriv Options REST: one-time WebSocket URL for OAuth2 (V2) sessions.
 * Matches PU `src/external/bot-skeleton/services/api/options-rest.ts` — Bearer must not be sent as
 * legacy `authorize` on `websockets/v3` (InputValidationFailed); connect using returned URL instead.
 *
 * URLs are stable production endpoints (same as New-docs Options Setup); intentionally not driven by env —
 * OTP response provides the ephemeral `wss://…` circuit URL.
 */

import { getOAuthDerivAppIdFromEverymanSync } from './config/config';

/** Production Options REST API host (staging would be explicit if Deriv documents it separately). */
const DERIV_OPTIONS_REST_ORIGIN = 'https://api.derivws.com';
const DERIV_OPTIONS_PATH_PREFIX = '/trading/v1/options';

export type TOptionsOtpWsUrlResponse = {
    url: string;
};

export async function requestOptionsOtpWsUrl(
    accountId: string,
    accessToken?: string
): Promise<TOptionsOtpWsUrlResponse> {
    if (!accountId?.trim()) {
        throw new Error('requestOptionsOtpWsUrl: accountId is required');
    }
    const token =
        accessToken ??
        (typeof localStorage !== 'undefined' ? localStorage.getItem('oauth2_access_token') || '' : '');
    if (!token) {
        throw new Error('requestOptionsOtpWsUrl: missing OAuth2 access token');
    }

    const derivAppId = getOAuthDerivAppIdFromEverymanSync();
    const appIdStr = String(derivAppId ?? '').trim();
    if (!appIdStr || !/^\d+$/.test(appIdStr)) {
        throw new Error(
            'requestOptionsOtpWsUrl: missing numeric Deriv-App-ID (botblitz_sync_oauth_deriv_app_id or config.app_id)'
        );
    }

    const url = `${DERIV_OPTIONS_REST_ORIGIN}${DERIV_OPTIONS_PATH_PREFIX}/accounts/${encodeURIComponent(accountId.trim())}/otp`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Deriv-App-ID': appIdStr,
            Accept: 'application/json',
        },
    });

    const text = await response.text();
    let json: unknown = null;
    if (text) {
        try {
            json = JSON.parse(text);
        } catch {
            throw new Error(`requestOptionsOtpWsUrl: non-JSON response (${response.status})`);
        }
    }

    if (!response.ok) {
        const body = json as { errors?: Array<{ message?: string }> } | null;
        const msg = body?.errors?.[0]?.message || `HTTP ${response.status}`;
        throw new Error(msg);
    }

    const body = json as { data?: { url?: string } };
    if (!body?.data?.url) {
        throw new Error('requestOptionsOtpWsUrl: response missing data.url');
    }
    return { url: body.data.url };
}
